import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import {
  addResearchAssistantMessages,
  ModelVersion,
  selectResearchAssistantIsWaitingForResponse,
  selectResearchAssistantModel,
  setResearchAssistantModel,
  useSetResearchAssistantToProcessing,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-cohorts';

import { emitUserPromptLegacy } from '../../namespaces/emitters/chatWindowEvents';

export const useResearchAssistantPromptState = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { projectId, cohortId, cohortVersionId } = useParams();
  const { user } = useAuth();

  const [userPrompt, setUserPrompt] = useState<string>('');
  const isWaitingForResponse = useAppSelector(selectResearchAssistantIsWaitingForResponse);
  const model = useAppSelector(selectResearchAssistantModel);
  const setResearchAssistantToProcessingWithAutoTimeout = useSetResearchAssistantToProcessing();
  const onUserPromptChange = (promptValue: string) => {
    setUserPrompt(promptValue);
  };

  const onModelChange = (modelVersion: ModelVersion) => {
    dispatch(setResearchAssistantModel(modelVersion));
    dispatch(
      addResearchAssistantMessages([
        {
          message: intl.formatMessage(
            {
              id: 'projects.cohortBuilderPage.researchAssistant.savedInfoMessage',
            },
            {
              oldModelVersion: model,
              newModelVersion: modelVersion,
            }
          ),
          timestamp: new Date().toISOString(),
          currentUser: 'user',
          isCurrentUser: true,
          type: ResearchAssistantMessageEnumLegacy.MODEL_CHANGE,
        },
      ])
    );
  };

  const sendUserPrompt = useCallback(() => {
    if (isWaitingForResponse) {
      return;
    }
    if (userPrompt.trim() === '') {
      return;
    }
    setResearchAssistantToProcessingWithAutoTimeout();
    dispatch(
      addResearchAssistantMessages([
        {
          message: userPrompt,
          timestamp: new Date().toLocaleTimeString(),
          currentUser: '',
          isCurrentUser: true,
        },
      ])
    );
    emitUserPromptLegacy({
      userPrompt,
      model,
      cohortId: cohortId || '',
      projectId: projectId || '',
      cohortVersionId: cohortVersionId || '',
      user,
    });
    setUserPrompt('');
  }, [
    cohortId,
    cohortVersionId,
    dispatch,
    isWaitingForResponse,
    model,
    projectId,
    setResearchAssistantToProcessingWithAutoTimeout,
    user,
    userPrompt,
  ]);

  return {
    userPrompt,
    onUserPromptChange,
    onModelChange,
    sendUserPrompt,
  };
};
